package com.example.positionahead.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.example.positionahead.domain.AlertEvaluator
import com.example.positionahead.model.AlertType
import com.example.positionahead.model.PriceAlert

@Composable
fun AlertsSection(
    alerts: List<PriceAlert>,
    onCreateAlert: (String, AlertType, Double, String) -> Unit,
    onDeleteAlert: (String) -> Unit,
    onToggleAlert: (String, Boolean) -> Unit,
    onClearTriggered: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    var symbol by remember { mutableStateOf("") }
    var threshold by remember { mutableStateOf("") }
    var note by remember { mutableStateOf("") }
    var alertType by remember { mutableStateOf(AlertType.PRICE_ABOVE) }

    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text("Price & G/L alerts", style = MaterialTheme.typography.titleMedium)
        Text(
            "Get notified in-app when a symbol hits your target. Educational tool only — not financial advice.",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
        )

        Card(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier.padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text("New alert", style = MaterialTheme.typography.titleSmall)
                OutlinedTextField(
                    value = symbol,
                    onValueChange = { symbol = it.uppercase() },
                    label = { Text("Symbol") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                )
                AlertTypeSelector(selected = alertType, onSelect = { alertType = it })
                OutlinedTextField(
                    value = threshold,
                    onValueChange = { threshold = it },
                    label = {
                        Text(
                            when (alertType) {
                                AlertType.PRICE_ABOVE, AlertType.PRICE_BELOW -> "Price ($)"
                                AlertType.GAIN_ABOVE_PCT, AlertType.LOSS_BELOW_PCT -> "Percent (%)"
                            },
                        )
                    },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                )
                OutlinedTextField(
                    value = note,
                    onValueChange = { note = it },
                    label = { Text("Note (optional)") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                )
                TextButton(
                    onClick = {
                        val value = threshold.toDoubleOrNull() ?: return@TextButton
                        if (symbol.isBlank()) return@TextButton
                        onCreateAlert(symbol.trim(), alertType, value, note)
                        symbol = ""
                        threshold = ""
                        note = ""
                    },
                ) { Text("Create alert") }
            }
        }

        if (alerts.isEmpty()) {
            Text("No alerts yet.", style = MaterialTheme.typography.bodyMedium)
        } else {
            alerts.forEach { alert ->
                AlertRow(
                    alert = alert,
                    onDelete = { onDeleteAlert(alert.id) },
                    onToggle = { enabled -> onToggleAlert(alert.id, enabled) },
                    onClearTriggered = { onClearTriggered(alert.id) },
                )
            }
        }
    }
}

@Composable
private fun AlertTypeSelector(
    selected: AlertType,
    onSelect: (AlertType) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text("Alert type", style = MaterialTheme.typography.labelMedium)
        AlertType.entries.forEach { type ->
            Row {
                TextButton(onClick = { onSelect(type) }) {
                    Text(
                        text = labelFor(type) + if (selected == type) " ✓" else "",
                        fontWeight = if (selected == type) FontWeight.SemiBold else FontWeight.Normal,
                    )
                }
            }
        }
    }
}

@Composable
private fun AlertRow(
    alert: PriceAlert,
    onDelete: () -> Unit,
    onToggle: (Boolean) -> Unit,
    onClearTriggered: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = if (alert.triggeredAt != null && !alert.read) {
                MaterialTheme.colorScheme.primaryContainer
            } else {
                MaterialTheme.colorScheme.surface
            },
        ),
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(AlertEvaluator.describe(alert), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.SemiBold)
            Text(
                if (alert.enabled) "Active" else "Paused",
                style = MaterialTheme.typography.labelSmall,
            )
            alert.triggeredAt?.let {
                Text("Triggered: $it", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary)
            }
            alert.note.takeIf { it.isNotBlank() }?.let {
                Text(it, style = MaterialTheme.typography.bodySmall)
            }
            Row {
                TextButton(onClick = { onToggle(!alert.enabled) }) {
                    Text(if (alert.enabled) "Pause" else "Enable")
                }
                if (alert.triggeredAt != null) {
                    TextButton(onClick = onClearTriggered) { Text("Reset") }
                }
                TextButton(onClick = onDelete) { Text("Delete") }
            }
        }
    }
}

private fun labelFor(type: AlertType): String = when (type) {
    AlertType.PRICE_ABOVE -> "Price above"
    AlertType.PRICE_BELOW -> "Price below"
    AlertType.GAIN_ABOVE_PCT -> "Gain above %"
    AlertType.LOSS_BELOW_PCT -> "Loss below %"
}
